import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import { useFinalizeVaultDocumentsBatchMutation } from "#graphql/supergraphHooks";
import {
  type FinalizeVaultDocumentInput,
  type FinalizeVaultDocumentsBatchMutation,
  type FinalizeVaultDocumentsBatchMutationVariables,
} from "#graphql/supergraphTypes";

export interface UseFinalizeVaultDocumentsBatchReturn {
  finalizeVaultDocumentsBatch: (
    inputs: FinalizeVaultDocumentInput[],
  ) => Promise<FinalizeVaultDocumentsBatchMutation["finalizeVaultDocumentsBatch"] | null>;
  loading: boolean;
  error: ReturnType<typeof useFinalizeVaultDocumentsBatchMutation>[1]["error"];
}

/**
 * DV-250 — step 3 of the BULK upload path: after every file's bytes are PUT to
 * its target, activate and validate the whole batch in one round trip. Returns
 * `{ documents, failed }` — `failed` carries the index + reason for any document
 * that couldn't be finalized. Usually driven via {@link useUploadVaultDocumentsBatch}.
 */
export const useFinalizeVaultDocumentsBatch = (
  options?: MutationHookOptions<
    FinalizeVaultDocumentsBatchMutation,
    FinalizeVaultDocumentsBatchMutationVariables
  >,
): UseFinalizeVaultDocumentsBatchReturn => {
  const [mutate, { loading, error }] = useFinalizeVaultDocumentsBatchMutation(options);

  const finalizeVaultDocumentsBatch = useCallback(
    async (inputs: FinalizeVaultDocumentInput[]) => {
      const { data } = await mutate({ variables: { inputs } });
      return data?.finalizeVaultDocumentsBatch ?? null;
    },
    [mutate],
  );

  return { finalizeVaultDocumentsBatch, loading, error };
};
